import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";
import type { SignalEventType } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";
import {
    COLUMN_AD_NETWORKS_ADDED_NAME,
    COLUMN_AD_NETWORKS_REMOVED_NAME,
    COLUMN_INTENT_SIGNAL_NAME,
    COLUMN_NEWS_NAME,
    COLUMN_SIGNALS_NAME,
    COLUMN_TECHNOLOGIES_ADDED_NAME,
    COLUMN_TECHNOLOGIES_REMOVED_NAME,
    COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL,
    COLUMN_TRAFFIC_COUNTRIES_NAME,
    COLUMN_ECOMMERCE_SIGNAL_DATA,
} from "app/pages/sales-intelligence/sub-modules/signals/constants/column-names";

import type { ColumnEventCategory, TrackingColumnName } from "../types";

import { getSignalsCountTrackingBucket } from "./utils";

export default function createSignalsColumnsTrackingService(track: TrackFn) {
    const trackSignalCellEvent = (action: string, eventName: string): void => {
        track("table", action, eventName);
    };

    const trackSignalDetailsRedirect = (category: ColumnEventCategory, eventSubName: string): void => {
        track(category, "click view details", `select view signal details/${eventSubName}`);
    };

    const trackCompanyOverviewRedirect = (category: ColumnEventCategory, eventSubName: string): void => {
        track(category, "click view in company overview", `select view in company overview/${eventSubName}`);
    };

    const trackSignalArticleClick = (
        category: ColumnEventCategory,
        eventSubName: string,
        eventSubSubName: string,
    ): void => {
        track(category, "click article link", `view article from tooltip/${eventSubName}/${eventSubSubName}`);
    };

    return {
        trackViewAllItems(columnName: TrackingColumnName) {
            switch (columnName) {
                case COLUMN_NEWS_NAME:
                    trackSignalDetailsRedirect("news signals", "view all");
                    break;
                case COLUMN_SIGNALS_NAME:
                    trackCompanyOverviewRedirect("any signals", "view all");
                    break;
                case COLUMN_TECHNOLOGIES_ADDED_NAME:
                case COLUMN_TECHNOLOGIES_REMOVED_NAME:
                case COLUMN_TECHNOLOGIES_WITH_POTENTIAL_RENEWAL:
                    trackSignalDetailsRedirect("technology signals", "view all technologies");
                    break;
                case COLUMN_TRAFFIC_COUNTRIES_NAME:
                    trackCompanyOverviewRedirect("traffic signals", "view all");
                    break;
                case COLUMN_ECOMMERCE_SIGNAL_DATA:
                    trackCompanyOverviewRedirect("ecommerce signals", "view all");
                    break;
                case COLUMN_INTENT_SIGNAL_NAME:
                    trackSignalDetailsRedirect("intent signals", "view all");
                    break;
                case COLUMN_AD_NETWORKS_ADDED_NAME:
                case COLUMN_AD_NETWORKS_REMOVED_NAME:
                    trackSignalDetailsRedirect("ad networks signal", "view all ad networks");
                    break;
            }
        },

        trackViewSignalType(columnName: TrackingColumnName, signalType: SignalEventType) {
            switch (columnName) {
                case COLUMN_NEWS_NAME:
                    trackSignalDetailsRedirect("news signals", signalType);
                    break;
                case COLUMN_SIGNALS_NAME:
                    trackCompanyOverviewRedirect("any signals", signalType);
                    break;
            }
        },

        trackViewSignalArticle(columnName: TrackingColumnName, signalType: SignalEventType, articleTitle: string) {
            switch (columnName) {
                case COLUMN_NEWS_NAME:
                    trackSignalArticleClick("news signals", signalType, articleTitle);
                    break;
                case COLUMN_SIGNALS_NAME:
                    trackSignalArticleClick("any signals", signalType, articleTitle);
                    break;
            }
        },

        trackSignalsCountTooltipOpen(domainSignalsCount: number): void {
            const signalsCountTrackingBucket = getSignalsCountTrackingBucket(domainSignalsCount);

            trackSignalCellEvent("mouse hover signals count", `show signals tooltip/${signalsCountTrackingBucket}`);
        },

        trackSignalsSidebarOpen(): void {
            trackSignalCellEvent("click", "signals");
        },
    };
}
